import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Truck, Lock } from "lucide-react";
import api from "@/api";
import { useStore, formatINR, formatDateRange } from "@/store/StoreContext";
import Seo from "@/components/store/Seo";
import { toast } from "sonner";
import { imgW, imgFallback } from "@/utils/img";

const STATES = ["Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"];

export default function Checkout() {
  const { cart, user, refreshCart } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: user?.email || "", phone: user?.phone || "", address: "", apartment: "", landmark: "", city: "", state: "", pin: "", country: "India", notes: "" });
  const [coupon, setCoupon] = useState("");
  const [paymentMode, setPaymentMode] = useState("cod");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, email: f.email || user.email, phone: f.phone || user.phone || "", first_name: f.first_name || user.name?.split(" ")[0] || "", last_name: f.last_name || user.name?.split(" ").slice(1).join(" ") || "" }));
  }, [user]);

  const fetchQuote = async (overrides = {}) => {
    try {
      const { data } = await api.post("/checkout/quote", { pin: form.pin, coupon, payment_mode: paymentMode, ...overrides });
      setQuote(data);
      return data;
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchQuote(); }, [paymentMode]);
  useEffect(() => { if (form.pin.length === 6) fetchQuote(); }, [form.pin]);

  if ((cart.items || []).length === 0 && !busy) {
    return (
      <div className="tq-container py-20 text-center" data-testid="checkout-empty">
        <p className="font-serif text-3xl mb-4">Your bag is empty</p>
        <Link to="/collections/new-arrivals" className="tq-btn-dark">Shop Now</Link>
      </div>
    );
  }

  const applyCoupon = async () => {
    if (!coupon.trim() || couponBusy) return;
    setCouponBusy(true);
    const q = await fetchQuote({ coupon: coupon.trim() });
    setCouponBusy(false);
    if (q?.coupon_error) toast.error(q.coupon_error);
    else if (q?.coupon) toast.success(`Coupon ${q.coupon.code} applied`);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...form, payment_mode: paymentMode, coupon: coupon.trim() });
      try { sessionStorage.setItem(`tq_order_email_${data.order.order_number}`, form.email); } catch (e) { /* silent */ }
      if (paymentMode === "razorpay" && data.razorpay) {
        const rzp = new window.Razorpay({
          key: data.razorpay.key_id,
          amount: data.razorpay.amount,
          currency: "INR",
          name: "TARISHQ FASHION",
          description: `Order ${data.order.order_number}`,
          order_id: data.razorpay.order_id,
          prefill: { name: data.order.customer.name, email: data.order.customer.email, contact: data.order.customer.phone },
          theme: { color: "#111111" },
          handler: async (resp) => {
            try {
              await api.post("/payments/razorpay/verify", { order_id: data.order.id, ...resp });
              await refreshCart();
              navigate(`/order-success/${data.order.order_number}`);
            } catch (err) { toast.error("Payment verification failed. Contact support."); }
          },
          modal: { ondismiss: () => { api.post("/payments/razorpay/failed", { order_id: data.order.id, reason: "Payment cancelled" }); toast.error("Payment cancelled — your order is saved as pending"); setBusy(false); } },
        });
        rzp.on("payment.failed", () => { api.post("/payments/razorpay/failed", { order_id: data.order.id }); toast.error("Payment failed. Please try again."); setBusy(false); });
        rzp.open();
      } else {
        await refreshCart();
        navigate(`/order-success/${data.order.order_number}`);
      }
    } catch (err) {
      toast.error(err.friendlyMessage);
      setBusy(false);
    }
  };

  const t = quote?.totals;
  return (
    <div className="tq-container py-8 sm:py-12" data-testid="checkout-page">
      <Seo title="Secure Checkout" noindex />
      <h1 className="font-serif text-3xl sm:text-5xl mb-8">Checkout</h1>
      <form onSubmit={placeOrder} className="grid lg:grid-cols-[1fr_400px] gap-10 items-start">
        <div className="space-y-8">
          <section className="tq-card p-6">
            <p className="font-serif text-xl mb-5 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#111111] text-[#FFFFFF] text-xs flex items-center justify-center">1</span> Contact</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="tq-label" htmlFor="co-email">Email *</label>
                <input id="co-email" type="email" required value={form.email} onChange={set("email")} data-testid="checkout-email" className="tq-input" /></div>
              <div><label className="tq-label" htmlFor="co-phone">Mobile *</label>
                <input id="co-phone" required pattern="[0-9+ ]{10,15}" value={form.phone} onChange={set("phone")} data-testid="checkout-phone" className="tq-input" placeholder="10-digit mobile" /></div>
            </div>
            {!user && <p className="text-xs text-[#C59B27] mt-3">Checking out as guest · <Link to="/account" className="underline">Login</Link> for faster checkout</p>}
          </section>
          <section className="tq-card p-6">
            <p className="font-serif text-xl mb-5 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#111111] text-[#FFFFFF] text-xs flex items-center justify-center">2</span> Shipping Address</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="tq-label" htmlFor="co-fname">First Name *</label>
                <input id="co-fname" required value={form.first_name} onChange={set("first_name")} data-testid="checkout-firstname" className="tq-input" /></div>
              <div><label className="tq-label" htmlFor="co-lname">Last Name</label>
                <input id="co-lname" value={form.last_name} onChange={set("last_name")} data-testid="checkout-lastname" className="tq-input" /></div>
              <div className="sm:col-span-2"><label className="tq-label" htmlFor="co-addr">Address *</label>
                <input id="co-addr" required value={form.address} onChange={set("address")} data-testid="checkout-address" className="tq-input" placeholder="House no., street, area" /></div>
              <div><label className="tq-label" htmlFor="co-apt">House / Apartment / Floor</label>
                <input id="co-apt" value={form.apartment} onChange={set("apartment")} data-testid="checkout-apartment" className="tq-input" /></div>
              <div><label className="tq-label" htmlFor="co-landmark">Landmark</label>
                <input id="co-landmark" value={form.landmark} onChange={set("landmark")} data-testid="checkout-landmark" className="tq-input" /></div>
              <div><label className="tq-label" htmlFor="co-city">City *</label>
                <input id="co-city" required value={form.city} onChange={set("city")} data-testid="checkout-city" className="tq-input" /></div>
              <div><label className="tq-label" htmlFor="co-state">State *</label>
                <select id="co-state" required value={form.state} onChange={set("state")} data-testid="checkout-state" className="tq-input bg-white">
                  <option value="">Select state</option>
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></div>
              <div><label className="tq-label" htmlFor="co-pin">PIN Code *</label>
                <input id="co-pin" required inputMode="numeric" maxLength={6} pattern="[0-9]{6}" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} data-testid="checkout-pin" className="tq-input" /></div>
              <div><label className="tq-label" htmlFor="co-country">Country *</label>
                <input id="co-country" required value={form.country} onChange={set("country")} data-testid="checkout-country" className="tq-input" /></div>
              <div className="sm:col-span-2"><label className="tq-label" htmlFor="co-notes">Order Notes</label>
                <input id="co-notes" value={form.notes} onChange={set("notes")} data-testid="checkout-notes" className="tq-input" placeholder="Optional" /></div>
            </div>
            {quote?.estimate && (
              <p className="mt-4 text-sm bg-[#FAFAFA] p-3 flex items-center gap-2" data-testid="checkout-estimate">
                <Truck size={15} className="text-[#A8821B]" /> Estimated delivery: <strong>{formatDateRange(quote.estimate)}</strong>{form.pin.length !== 6 && <span className="text-[#C59B27]">· enter PIN code for exact dates</span>}
              </p>
            )}
          </section>
          <section className="tq-card p-6">
            <p className="font-serif text-xl mb-5 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#111111] text-[#FFFFFF] text-xs flex items-center justify-center">3</span> Payment</p>
            <div className="space-y-3">
              <label className={`flex items-center justify-between gap-3 border p-4 cursor-pointer transition-colors ${paymentMode === "razorpay" ? "border-[#C59B27] bg-[#FAFAFA]" : "border-[#EAEAEA]"} ${quote?.razorpay_enabled ? "" : "opacity-50"}`}>
                <span className="flex items-center gap-3">
                  <input type="radio" name="payment" value="razorpay" checked={paymentMode === "razorpay"} disabled={!quote?.razorpay_enabled}
                    onChange={() => setPaymentMode("razorpay")} data-testid="pay-razorpay" className="accent-[#C59B27] w-4 h-4" />
                  <span>
                    <span className="block text-sm font-semibold">Pay Online — UPI, Cards, NetBanking, Wallets</span>
                    <span className="block text-xs text-[#C59B27] mt-0.5" data-testid="pay-razorpay-benefit">{quote?.razorpay_enabled ? (t?.prepaid_discount > 0 || t?.shipping_discount > 0 ? `Save extra ${formatINR((t.prepaid_discount || 0) + (t.shipping_discount || 0))} by paying online` : "Secure via Razorpay") : "Currently unavailable"}</span>
                  </span>
                </span>
                <Lock size={16} className="text-[#A8821B]" />
              </label>
              <label className={`flex items-center justify-between gap-3 border p-4 cursor-pointer transition-colors ${paymentMode === "cod" ? "border-[#C59B27] bg-[#FAFAFA]" : "border-[#EAEAEA]"}`}>
                <span className="flex items-center gap-3">
                  <input type="radio" name="payment" value="cod" checked={paymentMode === "cod"} onChange={() => setPaymentMode("cod")} data-testid="pay-cod" className="accent-[#C59B27] w-4 h-4" />
                  <span>
                    <span className="block text-sm font-semibold">Cash on Delivery</span>
                    <span className="block text-xs text-[#C59B27] mt-0.5">{t?.cod_fee ? `₹${t.cod_fee} handling fee` : "Pay when your order arrives"}</span>
                  </span>
                </span>
              </label>
            </div>
          </section>
        </div>

        <aside className="tq-card p-6 sticky top-28" data-testid="checkout-summary">
          <p className="font-serif text-2xl mb-5">Order Summary</p>
          <ul className="space-y-4 max-h-64 overflow-y-auto mb-5">
            {(quote?.items || cart.items || []).map((it) => (
              <li key={it.variant_id} className="flex gap-3">
                <div className="relative shrink-0">
                  <img src={imgW(it.image, 200)} alt={it.name} loading="lazy" onError={imgFallback} className="w-14 h-[4.5rem] object-cover bg-[#FAFAFA]" />
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#111111] text-white text-[10px] flex items-center justify-center">{it.qty}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-snug line-clamp-2">{it.name}</p>
                  <p className="text-[11px] text-[#C59B27]">Size {it.size}</p>
                </div>
                <p className="text-sm font-medium shrink-0">{formatINR(it.price * it.qty)}</p>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mb-5">
            <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input" className="tq-input !py-2.5 flex-1 uppercase" />
            <button type="button" onClick={applyCoupon} disabled={couponBusy} data-testid="coupon-apply-btn" className="tq-btn-outline !px-4 !py-2.5">Apply</button>
          </div>
          {quote?.coupon_error && <p className="text-xs text-[#8B1E21] -mt-3 mb-4" data-testid="coupon-error">{quote.coupon_error}</p>}
          {quote?.coupon && <p className="text-xs text-[#1A3C34] -mt-3 mb-4" data-testid="coupon-success">✓ {quote.coupon.code} applied</p>}
          {t && (
            <dl className="space-y-2.5 text-sm border-t border-[#EAEAEA] pt-4">
              <div className="flex justify-between"><dt className="text-[#666666]">Subtotal</dt><dd>{formatINR(t.subtotal)}</dd></div>
              {t.discount > 0 && <div className="flex justify-between text-[#1A3C34]"><dt>Discount</dt><dd>−{formatINR(t.discount)}</dd></div>}
              {t.prepaid_discount > 0 && <div className="flex justify-between text-[#1A3C34]"><dt>Prepaid Discount{t.prepaid_label ? ` · ${t.prepaid_label}` : ""}</dt><dd data-testid="checkout-prepaid-discount">−{formatINR(t.prepaid_discount)}</dd></div>}
              <div className="flex justify-between"><dt className="text-[#666666]">Shipping{t.ship_label ? ` · ${t.ship_label}` : ""}</dt><dd data-testid="checkout-shipping">{t.shipping_discount > 0 ? <><span className="line-through text-[#8E8E93] mr-1.5">{formatINR(t.shipping_base)}</span><span className="text-[#1A3C34] font-medium">{t.shipping_fee ? formatINR(t.shipping_fee) : "FREE"}</span></> : t.shipping_fee ? formatINR(t.shipping_fee) : "FREE"}</dd></div>
              {(t.savings || 0) > 0 && <div className="flex justify-between text-[#1A3C34] text-xs"><dt>Total savings on this order</dt><dd data-testid="checkout-savings">−{formatINR(t.savings)}</dd></div>}
              {t.cod_fee > 0 && <div className="flex justify-between"><dt className="text-[#666666]">COD Fee</dt><dd>{formatINR(t.cod_fee)}</dd></div>}
              <div className="flex justify-between text-base font-semibold border-t border-[#EAEAEA] pt-3"><dt>Total</dt><dd data-testid="checkout-total">{formatINR(t.total)}</dd></div>
            </dl>
          )}
          <button type="submit" disabled={busy} data-testid="place-order-btn" className="tq-btn-dark w-full mt-6">
            {busy ? "Placing Order…" : paymentMode === "razorpay" ? "Pay Securely" : "Place Order (COD)"}
          </button>
          <p className="flex items-center justify-center gap-2 text-[11px] text-[#8E8E93] mt-4"><ShieldCheck size={14} /> 256-bit SSL encrypted · Secure payments by Razorpay</p>
        </aside>
      </form>
    </div>
  );
}
